import { type PromoFeatureAccess, type PromoFeatureWithAccess } from '@luxuryescapes/contract-svc-promo';

export type PromoFeatureIndicator = PromoFeatureWithAccess & {
  depth: number;
  parentKeys: ReadonlyArray<string>;
};

function getParentFeatureKey(feature: PromoFeatureWithAccess): string | undefined {
  return feature.tags?.find((tag) => tag.type === 'parent-feature')?.key;
}

export function getPromoFeatureAccessColor(access: PromoFeatureAccess): 'success' | 'warning' | 'error' {
  return access === 'unknown' ? 'warning' : access ? 'success' : 'error';
}

/** Adds display depth to the flat feature catalogue using parent-feature tags. */
export function getPromoFeatureIndicators(
  features: ReadonlyArray<PromoFeatureWithAccess>,
): ReadonlyArray<PromoFeatureIndicator> {
  const featureKeys = new Set(features.map((feature) => feature.key));
  const childrenByParentKey = new Map<string, Array<PromoFeatureWithAccess>>();
  const roots: Array<PromoFeatureWithAccess> = [];

  features.forEach((feature) => {
    const parentKey = getParentFeatureKey(feature);

    // Features whose parent is missing from the catalogue are shown as roots rather than dropped.
    if (parentKey == null || parentKey === feature.key || !featureKeys.has(parentKey)) {
      roots.push(feature);
      return;
    }

    childrenByParentKey.set(parentKey, [...(childrenByParentKey.get(parentKey) ?? []), feature]);
  });

  const indicators: Array<PromoFeatureIndicator> = [];
  const visitedKeys = new Set<string>();

  function visit(feature: PromoFeatureWithAccess, depth: number, parentKeys: ReadonlyArray<string>) {
    if (visitedKeys.has(feature.key)) {
      return;
    }

    visitedKeys.add(feature.key);
    indicators.push({ ...feature, depth, parentKeys });

    (childrenByParentKey.get(feature.key) ?? []).forEach((child) => {
      visit(child, depth + 1, [...parentKeys, feature.key]);
    });
  }

  roots.forEach((feature) => visit(feature, 0, []));
  // Features caught in a parent cycle are never reached from a root; list them as roots too.
  features.forEach((feature) => visit(feature, 0, []));
  return indicators;
}

export function findPromoFeatureIndicator(
  features: ReadonlyArray<PromoFeatureWithAccess>,
  featureKey: string,
): PromoFeatureIndicator | undefined {
  return getPromoFeatureIndicators(features).find((feature) => feature.key === featureKey);
}

export function getOptInFeatureKeys(features: ReadonlyArray<PromoFeatureWithAccess>): ReadonlySet<string> {
  return new Set(features.flatMap((feature) => (feature.canOptIn ? [feature.key] : [])));
}

export const promoFeatureRolloutGroups = ['Opt-in / test only', 'Limited rollout', 'Generally available'] as const;

/** Catalogue rollout describes deployment; canUse only describes this user's role access. */
export function getPromoFeatureRolloutGroup(feature: PromoFeatureIndicator) {
  const status = `${feature.experimentalStatus} ${feature.currentStatusHeadline}`.toLowerCase();

  if (
    feature.isOnlyTest ||
    feature.canOptIn ||
    feature.accessReasons.some((reason) => reason.requirement.type === 'optIn') ||
    /disabled|off by default|not functional|in development|opt-in/.test(status)
  ) {
    return promoFeatureRolloutGroups[0];
  }

  if (
    feature.accessReasons.length > 0 ||
    feature.requiredRoles?.roles.length > 0 ||
    /limited|beta|testing|pending|indicative/.test(status)
  ) {
    return promoFeatureRolloutGroups[1];
  }

  return promoFeatureRolloutGroups[2];
}
